import { showModal } from 'modal.react.js';
import { useDispatch, useSelector } from 'react-redux';

import area from 'components/shared/GlobalModalArea/area';
import AlertModal from 'components/shared/GlobalModalArea/Modal/AlertModal';
import i18n from 'i18n';
import { editThunk, uploadThunk } from 'store/assets/thunks';
import { itinerariesItemDataSelector } from 'store/itinerariesItem/selectors';
import { ASSET_STORAGE_TYPES, ASSET_TYPES } from 'utils/asset';
import { updateArrayById } from 'utils/immutableFunctions';

import s from './index.module.css';

export const GUEST_PORTAL_ASSET_RECOMMENDED_MAX_SIZE = 10;

export const useAssetsHandlers = () => {
  const dispatch = useDispatch();
  const guestPortal = useSelector(itinerariesItemDataSelector);

  const handleUploadAssets = async ({ value, onChange, category, infoAlertText }) => {
    const assets = await dispatch(
      uploadThunk({
        modalTitle: i18n.t('proposal_content.file.upload_modal.title'),
        dropZonePlaceholder: i18n.t('proposal_content.file.upload_modal.section.title'),
        namePlaceholder: i18n.t('proposal_content.file.enter_name'),
        isSelectedEnabled: false,
        type: ASSET_TYPES.FILE,
        isCenterOfImageEnabled: false,
        withTags: false,
        withAssetSettings: false,
        isSortingEnabled: false,
        recommendedFileSize: GUEST_PORTAL_ASSET_RECOMMENDED_MAX_SIZE,
        fileExtraParams: {
          category,
          guestPortalId: guestPortal.id,
        },
        storageType: ASSET_STORAGE_TYPES.DOCUMENTS,
        saveButtonText: i18n.t('proposal_content.file.save_to_guest_portal', { defaultValue: 'Save to Guest Portal' }),
        multipleSaveButtonText: i18n.t('proposal_content.file.save_to_guest_portal', {
          defaultValue: 'Save to Guest Portal',
        }),
        multiple: true,
        translate: true,
        infoAlertText: infoAlertText,
      }),
    );

    if (assets === null) {
      return;
    }

    onChange([...(value ?? []), ...assets]);
  };

  const handleDeleteAsset = (asset, { value, onChange }) => {
    showModal(area, (res) => (
      <AlertModal
        headerText={i18n.t('proposal_content.file.delete_file')}
        confirmButtonText={i18n.t('common.buttons.delete')}
        closeButtonText={i18n.t('common.buttons.cancel')}
        res={res}
        onAccept={async () => {
          const files = value ?? [];
          const filteredFiles = files.filter((file) => file.id !== asset.id);
          onChange(filteredFiles);
          res();
        }}
        bodyText={i18n.t('proposal_content.file.confirmation.delete_attached_file')}
        wrapperClassName={s.alertModalWrapper}
      />
    ));
  };

  const handleDeleteAssets = ({ form, fieldNames }) => {
    return showModal(area, (res) => (
      <AlertModal
        headerText="Confirmation"
        confirmButtonText="Yes, Delete"
        closeButtonText="Cancel"
        footerClassName={s.alertModalButtons}
        res={res}
        onAccept={() => {
          fieldNames.forEach((fieldName) => {
            if (fieldName === 'additionalLinks') {
              const files = form?.getState()?.values[fieldName] ?? [];
              const filesToDestroy = files.map(({ id }) => ({ id, _destroy: true }));
              form.change(fieldName, filesToDestroy);
            } else {
              form.change(fieldName, []);
            }
          });
          res();
        }}
        bodyText={<div className={s.bodyText}>Are you sure you want to clear?</div>}
      />
    ));
  };

  const onAfterEdited = (asset, { value, onChange }) => {
    const files = value ?? [];
    onChange(updateArrayById(files, asset.id, () => asset));
  };

  const handleEditAsset = (id, { value, onChange }) => {
    dispatch(
      editThunk({
        id,
        modalTitle: i18n.t('proposal_content.file.edit_file'),
        saveButtonText: i18n.t('proposal_content.file.save_to_guest_portal'),
        isCenterOfImageEnabled: false,
        withTags: false,
        withAssetSettings: false,
        onDelete: (asset) => handleDeleteAsset(asset, { value, onChange }),
        storageType: ASSET_STORAGE_TYPES.DOCUMENTS,
        onAfterEdited: (asset) => onAfterEdited(asset, { value, onChange }),
        namePlaceholder: i18n.t('proposal_content.file.enter_name'),
        translate: true,
      }),
    );
  };

  return {
    handleUploadAssets,
    handleDeleteAsset,
    handleDeleteAssets,
    handleEditAsset,
  };
};
